import React, { useState, useRef } from 'react';
import {
	Container,
	TextField,
	Button,
	Box,
	Typography,
	Paper,
	Grid,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	CircularProgress,
	Menu,
	Snackbar,
	Alert,
	MenuItem,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LinkIcon from '@mui/icons-material/Link';
import SaveIcon from '@mui/icons-material/Save';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import ClearIcon from '@mui/icons-material/Clear';
import CodeIcon from '@mui/icons-material/Code';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { styled } from '@mui/material/styles';

const StyledPaper = styled(Paper)(({ theme }) => ({
	padding: theme.spacing(3),
	height: '330px',
	display: 'flex',
	flexDirection: 'column',
	backgroundColor: '#f8f9fa',
}));

const HiddenInput = styled('input')({
	display: 'none',
});

function JsonEscapeTools() {
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: '',
		severity: 'success',
	});
	const [input, setInput] = useState('');
	const [output, setOutput] = useState('');
	const [urlModalOpen, setUrlModalOpen] = useState(false);
	const [sampleModalOpen, setSampleModalOpen] = useState(false);
	const [jsonUrl, setJsonUrl] = useState('');
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [saveAnchorEl, setSaveAnchorEl] = useState(null);
	const saveMenuOpen = Boolean(saveAnchorEl);
	const fileInputRef = useRef(null);

	// Örnek JSON verileri
	const sampleJsonData = {
		simple: {
			name: 'John Doe',
			age: 30,
			email: 'john@example.com',
			isStudent: false,
			hobbies: ['reading', 'swimming', 'coding'],
			address: {
				street: '123 Main St',
				city: 'New York',
				country: 'USA',
			},
		},
		complex: {
			id: 1,
			timestamp: '2024-01-20T10:30:00Z',
			user: {
				id: 'usr_123',
				profile: {
					firstName: 'John',
					lastName: 'Doe',
					email: 'john@example.com',
					preferences: {
						theme: 'dark',
						notifications: true,
						language: 'en-US',
					},
				},
				settings: {
					privacy: {
						isPublic: false,
						showEmail: true,
					},
				},
			},
			items: [
				{
					id: 'item_1',
					name: 'Product A',
					price: 29.99,
					tags: ['electronics', 'new'],
					metadata: {
						color: 'black',
						weight: '300g',
						dimensions: {
							width: 10,
							height: 15,
							depth: 5,
						},
					},
				},
				{
					id: 'item_2',
					name: 'Product B',
					price: 49.99,
					tags: ['accessories', 'featured'],
					inStock: true,
				},
			],
			statistics: {
				views: 1500,
				likes: 240,
				shares: {
					facebook: 100,
					twitter: 50,
					linkedin: 90,
				},
			},
		},
	};
	const handleEscape = () => {
		try {
			const escaped = JSON.stringify(input);
			setOutput(escaped.slice(1, -1));
			setSnackbar({
				open: true,
				message: 'Text escaped successfully!',
				severity: 'success',
			});
		} catch (error) {
			setOutput('Error escaping text');
			setSnackbar({
				open: true,
				message: 'Error escaping text!',
				severity: 'error',
			});
		}
	};

	const handleUnescape = () => {
		try {
			const unescaped = JSON.parse(`"${input}"`);
			setOutput(unescaped);
			setSnackbar({
				open: true,
				message: 'Text unescaped successfully!',
				severity: 'success',
			});
		} catch (error) {
			setOutput('Cannot unescape this entry');
			setSnackbar({
				open: true,
				message: 'Cannot unescape this entry!',
				severity: 'error',
			});
		}
	};
	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(output);
			setInput('');
			setSnackbar({
				open: true,
				message: 'Content copied to clipboard!',
				severity: 'success',
			});
		} catch (error) {
			console.error('Failed to copy:', error);
			setSnackbar({
				open: true,
				message: 'Failed to copy content!',
				severity: 'error',
			});
		}
	};

	const handleSnackbarClose = (event, reason) => {
		if (reason === 'clickaway') {
			return;
		}
		setSnackbar({ ...snackbar, open: false });
	};

	const handleFileUpload = (event) => {
		const file = event.target.files[0];
		if (file) {
			const reader = new FileReader();
			reader.onload = (e) => {
				try {
					const content = e.target.result;
					const jsonContent = JSON.parse(content);
					setInput(JSON.stringify(jsonContent, null, 2));
				} catch (error) {
					setInput('Error reading JSON file: ' + error.message);
				}
			};
			reader.readAsText(file);
		}
	};

	const handleUploadClick = () => {
		fileInputRef.current.click();
	};

	const handleUrlModalOpen = () => {
		setUrlModalOpen(true);
		setError('');
	};

	const handleUrlModalClose = () => {
		setUrlModalOpen(false);
		setJsonUrl('');
		setError('');
	};

	const handleSampleModalOpen = () => setSampleModalOpen(true);
	const handleSampleModalClose = () => setSampleModalOpen(false);

	const handleSaveClick = (event) => {
		setSaveAnchorEl(event.currentTarget);
	};

	const handleSaveMenuClose = () => {
		setSaveAnchorEl(null);
	};

	const loadSampleJson = (type) => {
		setInput(JSON.stringify(sampleJsonData[type], null, 2));
		handleSampleModalClose();
	};

	const handleSaveFile = (fileType) => {
		try {
			let content = output;
			let fileExtension = fileType;
			let mimeType = fileType === 'json' ? 'application/json' : 'text/plain';

			if (fileType === 'json') {
				try {
					JSON.parse(output);
					content = JSON.stringify(JSON.parse(output), null, 2);
				} catch {
					content = output;
				}
			}

			const blob = new Blob([content], { type: mimeType });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = `processed_file.${fileExtension}`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
			handleSaveMenuClose();

			setSnackbar({
				open: true,
				message: `File saved successfully as ${fileType.toUpperCase()}!`,
				severity: 'success',
			});
		} catch (error) {
			setSnackbar({
				open: true,
				message: 'Error saving file: ' + error.message,
				severity: 'error',
			});
			handleSaveMenuClose();
		}
	};

	const fetchJsonFromUrl = async () => {
		setLoading(true);
		setError('');

		try {
			const response = await fetch(jsonUrl);
			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}
			const data = await response.json();
			setInput(JSON.stringify(data, null, 2));
			handleUrlModalClose();
		} catch (error) {
			setError(`Error fetching JSON: ${error.message}`);
		} finally {
			setLoading(false);
		}
	};

	const handleClearAll = () => {
		setInput('');
		setOutput('');
		setJsonUrl('');
		setError('');
		setSnackbar({
			open: true,
			message: 'All content cleared!',
			severity: 'info',
		});
	};

	const handleClearResult = () => {
		setOutput('');
		setSnackbar({
			open: true,
			message: 'Result cleared!',
			severity: 'info',
		});
	};

	return (
		<Container maxWidth="xl">
			<Box sx={{ my: 4 }}>
				<Typography variant="h4" component="h1" gutterBottom>
					JSON Escape/Unescape Tool
				</Typography>

				<Typography  variant="body1" color="text.secondary" paragraph>
				JSON Escape/Unescape Tool is a fast and practical solution to solve character escape problems you may encounter when processing JSON data. With this tool, you can properly format your JSON data and use it according to your needs without any problems.				</Typography>

				<Grid sx={{mt:2}} container spacing={3}>
					<Grid item xs={12} md={6}>
						<Box
							sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 0 } }}
						>
							<Typography variant="h6" component="h2">
								Input
							</Typography>
							<Box sx={{ display: 'flex', gap: 1 }}>
								<Button variant="outlined" startIcon={<CodeIcon />} onClick={handleSampleModalOpen} size="small">
									Sample JSON
								</Button>
								<Button variant="outlined" startIcon={<LinkIcon />} onClick={handleUrlModalOpen} size="small">
									With URL
								</Button>
								<Button variant="outlined" startIcon={<UploadFileIcon />} onClick={handleUploadClick} size="small">
									Upload JSON
								</Button>
							</Box>
							<HiddenInput type="file" accept=".json" ref={fileInputRef} onChange={handleFileUpload} />
						</Box>
						<StyledPaper elevation={3}>
							<TextField
								fullWidth
								multiline
								rows={10}
								value={input}
								onChange={(e) => setInput(e.target.value)}
								variant="outlined"
								sx={{ flex: 1 }}
								placeholder="Enter JSON text, upload a file, or fetch from URL"
							/>
							<Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
								<Button variant="contained" color="primary" onClick={handleEscape} disabled={!input.trim()} fullWidth>
									Escape
								</Button>
								<Button variant="contained" color="secondary" onClick={handleUnescape} disabled={!input.trim()} fullWidth>
									Unescape
								</Button>
							</Box>
						</StyledPaper>
					</Grid>

					<Grid item xs={12} md={6}>
						<Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
							<Typography variant="h6" component="h2">
								Result
							</Typography>
							<Box sx={{ display: 'flex', gap: 1 }}>
								<IconButton onClick={handleCopy} color="primary" size="small" title="Copy to clipboard">
									<ContentCopyIcon />
								</IconButton>
								<IconButton onClick={handleClearResult} color="error" size="small" title="Clear result" disabled={!output}>
									<ClearIcon />
								</IconButton>
							</Box>
						</Box>
						<StyledPaper gutterBottom elevation={3}>
							<TextField
								fullWidth
								multiline
								rows={10}
								value={output}
								variant="outlined"
								sx={{ flex: 1 }}
								InputProps={{
									readOnly: true,
								}}
							/>
							<Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
								<Button
									variant="contained"
									color="primary"
									startIcon={<SaveIcon />}
									endIcon={<KeyboardArrowDownIcon />}
									onClick={handleSaveClick}
									disabled={!output}
									fullWidth
									sx={{
										width: '100%',
										position: 'relative',
										zIndex: 1,
									}}
								>
									Save as File
								</Button>
								<Menu
									anchorEl={saveAnchorEl}
									open={saveMenuOpen}
									onClose={handleSaveMenuClose}
									anchorOrigin={{
										vertical: 'bottom',
										horizontal: 'left',
									}}
									transformOrigin={{
										vertical: 'top',
										horizontal: 'left',
									}}
									PaperProps={{
										sx: {
											width: saveAnchorEl ? saveAnchorEl.offsetWidth : 'auto',
											marginTop: '2px',
										},
									}}
								>
									<MenuItem
										onClick={() => handleSaveFile('json')}
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: 1,
										}}
									>
										Save as JSON
									</MenuItem>
									<MenuItem
										onClick={() => handleSaveFile('txt')}
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: 1,
										}}
									>
										Save as TXT
									</MenuItem>
								</Menu>
								<Button
									variant="outlined"
									color="error"
									startIcon={<DeleteSweepIcon />}
									onClick={handleClearAll}
									disabled={!input && !output}
									fullWidth
								>
									Clear All
								</Button>
							</Box>
						</StyledPaper>
					</Grid>
				</Grid>

				{/* URL Modal */}
				<Dialog open={urlModalOpen} onClose={handleUrlModalClose} maxWidth="sm" fullWidth>
					<DialogTitle>Enter JSON URL</DialogTitle>
					<DialogContent>
						<TextField
							autoFocus
							margin="dense"
							label="JSON URL"
							type="url"
							fullWidth
							variant="outlined"
							value={jsonUrl}
							onChange={(e) => setJsonUrl(e.target.value)}
							error={!!error}
							helperText={error}
							disabled={loading}
						/>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleUrlModalClose} disabled={loading}>
							Cancel
						</Button>
						<Button
							onClick={fetchJsonFromUrl}
							variant="contained"
							disabled={!jsonUrl || loading}
							startIcon={loading && <CircularProgress size={20} />}
						>
							{loading ? 'Loading...' : 'Fetch JSON'}
						</Button>
					</DialogActions>
				</Dialog>

				{/* Sample JSON Modal */}
				<Dialog open={sampleModalOpen} onClose={handleSampleModalClose} maxWidth="xs" fullWidth>
					<DialogTitle>Choose Sample JSON</DialogTitle>
					<DialogContent>
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
							<Button variant="outlined" onClick={() => loadSampleJson('simple')} fullWidth>
								Simple JSON Example
							</Button>
							<Button variant="outlined" onClick={() => loadSampleJson('complex')} fullWidth>
								Complex JSON Example
							</Button>
						</Box>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleSampleModalClose}>Cancel</Button>
					</DialogActions>
				</Dialog>
			</Box>
			<Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
				<Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Container>
	);
}

export default JsonEscapeTools;
